import { useIntl } from 'react-intl';

import { PresetKey, PresetProps } from './types';

export const usePreset = (presetKey: PresetKey): PresetProps => {
  const intl = useIntl();
  switch (presetKey) {
    case 'page-access':
      return {
        description: intl.formatMessage({
          id: 'ErrorPage.pageAccess.description',
          defaultMessage:
            'You do not have permission to access this page. To request permission, contact your organization administrator.',
          description:
            'Description for error page when user lacks access to a specific page.',
        }),
        image: 'error-page-access' as const,
        imageAlt: 'page access error',
        resolveActionText: intl.formatMessage({
          id: 'ErrorPage.pageAccess.resolveActionText',
          defaultMessage: 'Take me home',
          description: 'Button text for resolving page access error.',
        }),
        title: intl.formatMessage({
          id: 'ErrorPage.pageAccess.title',
          defaultMessage: 'No Access to Page',
          description:
            'Title for error page when user lacks access to a specific page.',
        }),
      };

    case 'page-error':
      return {
        description: intl.formatMessage({
          id: 'ErrorPage.pageError.description',
          defaultMessage:
            'There was an error with our system. Please return to Home to access Evisort.',
          description: 'Description for generic page error.',
        }),
        image: 'error-page-error' as const,
        imageAlt: 'page error',
        resolveActionText: intl.formatMessage({
          id: 'ErrorPage.pageError.resolveActionText',
          defaultMessage: 'Take me home',
          description: 'Button text for resolving generic page error.',
        }),
        title: intl.formatMessage({
          id: 'ErrorPage.pageError.title',
          defaultMessage: 'Page Error',
          description: 'Title for generic page error.',
        }),
      };

    case 'page-not-found':
      return {
        description: intl.formatMessage({
          id: 'ErrorPage.pageNotFound.description',
          defaultMessage:
            'We could not find this page. Please return to Home to access Evisort.',
          description: 'Description for page not found error.',
        }),
        image: 'error-page-not-found' as const,
        imageAlt: 'page not found error',
        resolveActionText: intl.formatMessage({
          id: 'ErrorPage.pageNotFound.resolveActionText',
          defaultMessage: 'Take me home',
          description: 'Button text for resolving page not found error.',
        }),
        title: intl.formatMessage({
          id: 'ErrorPage.pageNotFound.title',
          defaultMessage: 'Page Not Found',
          description: 'Title for page not found error.',
        }),
      };

    case 'sso-access':
      return {
        description: intl.formatMessage({
          id: 'ErrorPage.ssoAccess.description',
          defaultMessage:
            "You don't have access to the system. Please contact your access administrator to request access to Evisort.",
          description: 'Description for SSO access error.',
        }),
        image: 'error-sso-access' as const,
        imageAlt: 'sso access error',
        resolveActionText: null,
        title: intl.formatMessage({
          id: 'ErrorPage.ssoAccess.title',
          defaultMessage: 'No SSO Access to System',
          description: 'Title for SSO access error.',
        }),
      };

    case 'service-unavailable':
      return {
        description: intl.formatMessage({
          id: 'ErrorPage.serviceUnavailable.description',
          defaultMessage: 'Please retry later.',
          description: 'Description for service unavailable error.',
        }),
        image: 'error-service-unavailable' as const,
        imageAlt: 'service unavailable',
        resolveActionText: null,
        title: intl.formatMessage({
          id: 'ErrorPage.serviceUnavailable.title',
          defaultMessage: 'Service is temporarily unavailable.',
          description: 'Title for service unavailable error.',
        }),
      };

    case 'system-access':
      return {
        description: intl.formatMessage({
          id: 'ErrorPage.systemAccess.description',
          defaultMessage:
            "You don't have access to the system. Please sign in with the proper credentials to use Evisort. If you feel you should access to Evisort, contact customer support.",
          description: 'Description for system access error.',
        }),
        image: 'error-system-access' as const,
        imageAlt: 'system access error',
        resolveActionText: intl.formatMessage({
          id: 'ErrorPage.systemAccess.resolveActionText',
          defaultMessage: 'Sign In',
          description: 'Button text for resolving system access error.',
        }),
        title: intl.formatMessage({
          id: 'ErrorPage.systemAccess.title',
          defaultMessage: 'No Access to System',
          description: 'Title for system access error.',
        }),
      };
    default:
      return {
        description: '',
        image: undefined,
        imageAlt: '',
        resolveActionText: null,
        title: '',
      };
  }
};
